import {
  PLAY_STORE_LIVE,
  IOS_APP_STORE_LIVE,
  APP_STORE_URL,
  PLAY_STORE_URL,
  appComingSoonLabel,
} from "@shared/appStore";

/**
 * App-store badges for the lamirage.co landing
 * (spec docs/superpowers/specs/2026-10-07-lamirage-co-landing-app-launch-design.md §1).
 *
 * Gated PER STORE (shared/appStore.ts). The App Store badge and link render ONLY when
 * IOS_APP_STORE_LIVE — Apple's badge guidelines forbid the badge before the app is available.
 * The Google Play badge renders when PLAY_STORE_LIVE. When exactly one store is live, the other
 * gets a small "Coming soon to …" caption; when neither is, only the gold outline pill shows.
 * `play` / `ios` exist so tests can render every state; pages pass nothing.
 */
export default function StoreBadges({
  play = PLAY_STORE_LIVE,
  ios = IOS_APP_STORE_LIVE,
  className = "",
}: {
  play?: boolean;
  ios?: boolean;
  className?: string;
}) {
  const soon = appComingSoonLabel({ play, ios });
  if (!play && !ios) {
    return (
      <span
        data-testid="store-badges-coming-soon"
        className={`inline-flex items-center gap-2 rounded-full border border-[#efb700]/60 px-5 py-2.5 text-sm font-semibold text-[#efb700] ${className}`}
      >
        <span className="h-1.5 w-1.5 rounded-full bg-[#efb700]" aria-hidden />
        {soon}
      </span>
    );
  }
  return (
    <div data-testid="store-badges-live" className={`flex flex-wrap items-center gap-3 ${className}`}>
      {ios && (
        <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="inline-block">
          <img
            src="/app-launch/badge-appstore.svg"
            alt="Download on the App Store"
            width={135}
            height={45}
            className="block h-[45px] w-auto"
          />
        </a>
      )}
      {play && (
        <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="inline-block">
          <img
            src="/app-launch/badge-googleplay.png"
            alt="Get it on Google Play"
            width={152}
            height={59}
            className="block h-[59px] w-auto -my-[7px]"
          />
        </a>
      )}
      {soon && (
        <span
          data-testid="store-badges-partial-soon"
          className="inline-flex items-center gap-2 rounded-full border border-[#efb700]/50 px-3.5 py-1.5 text-xs font-semibold text-[#efb700]"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#efb700]" aria-hidden />
          {soon}
        </span>
      )}
    </div>
  );
}
